import { cn } from '@/lib/utils'

export type CatMood = 'happy' | 'neutral' | 'angry' | 'curious' | 'sleepy'

interface CatProps {
  mood?: CatMood
  size?: number
  className?: string
  /** Accessible label; decorative when omitted. */
  title?: string
}

/**
 * The Dovira cats (angry / neutral / happy from the kiosk, plus curious and sleepy).
 * Pure SVG coloured with theme tokens so they work in light and dark mode.
 */
export function Cat({ mood = 'neutral', size = 96, className, title }: CatProps) {
  const fur = 'var(--cat-fur)'
  const fur2 = 'var(--cat-fur-2)'
  const line = 'var(--cat-line)'
  const tilt = mood === 'curious' ? -8 : 0
  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title && <title>{title}</title>}
      <ellipse cx="60" cy="108" rx="34" ry="5" fill={line} opacity="0.08" />
      <g transform={`rotate(${tilt} 60 64)`}>
        {/* ears */}
        <path d="M24 44 L30 10 L52 32 Z" fill={fur} stroke={line} strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M96 44 L90 10 L68 32 Z" fill={fur} stroke={line} strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M31 36 L33 19 L44 31 Z" fill={fur2} />
        <path d="M89 36 L87 19 L76 31 Z" fill={fur2} />
        {/* head */}
        <ellipse cx="60" cy="64" rx="40" ry="36" fill={fur} stroke={line} strokeWidth="2.5" />
        {/* muzzle */}
        <ellipse cx="60" cy="76" rx="17" ry="12" fill={fur2} opacity="0.9" />
        {/* cheeks */}
        {mood === 'happy' && (
          <>
            <ellipse cx="34" cy="74" rx="6" ry="3.5" fill="#fff" opacity="0.35" />
            <ellipse cx="86" cy="74" rx="6" ry="3.5" fill="#fff" opacity="0.35" />
          </>
        )}
        <Eyes mood={mood} line={line} />
        {/* nose */}
        <path d="M56 70 L64 70 L60 75 Z" fill={line} strokeLinejoin="round" />
        <Mouth mood={mood} line={line} />
        {/* whiskers */}
        <g stroke={line} strokeWidth="1.6" strokeLinecap="round" opacity="0.7">
          <path d="M40 74 L18 70" />
          <path d="M40 79 L18 82" />
          <path d="M80 74 L102 70" />
          <path d="M80 79 L102 82" />
        </g>
        {mood === 'curious' && (
          <text x="96" y="26" fontSize="20" fontWeight="700" fill={line} fontFamily="Inter, sans-serif">
            ?
          </text>
        )}
        {mood === 'sleepy' && (
          <text x="92" y="28" fontSize="14" fontWeight="700" fill={line} fontFamily="Inter, sans-serif">
            z z
          </text>
        )}
      </g>
    </svg>
  )
}

function Eyes({ mood, line }: { mood: CatMood; line: string }) {
  switch (mood) {
    case 'happy':
      return (
        <g stroke={line} strokeWidth="3" fill="none" strokeLinecap="round">
          <path d="M38 60 Q45 52 52 60" />
          <path d="M68 60 Q75 52 82 60" />
        </g>
      )
    case 'angry':
      return (
        <g>
          <path d="M36 50 L52 56" stroke={line} strokeWidth="3" strokeLinecap="round" />
          <path d="M84 50 L68 56" stroke={line} strokeWidth="3" strokeLinecap="round" />
          <ellipse cx="45" cy="62" rx="5" ry="4" fill={line} />
          <ellipse cx="75" cy="62" rx="5" ry="4" fill={line} />
        </g>
      )
    case 'sleepy':
      return (
        <g stroke={line} strokeWidth="3" fill="none" strokeLinecap="round">
          <path d="M38 60 Q45 65 52 60" />
          <path d="M68 60 Q75 65 82 60" />
        </g>
      )
    case 'curious':
      return (
        <g>
          <circle cx="45" cy="59" r="7" fill="#fff" stroke={line} strokeWidth="2" />
          <circle cx="75" cy="59" r="7" fill="#fff" stroke={line} strokeWidth="2" />
          <circle cx="47" cy="57" r="3.5" fill={line} />
          <circle cx="77" cy="57" r="3.5" fill={line} />
        </g>
      )
    default:
      return (
        <g>
          <ellipse cx="45" cy="60" rx="4.5" ry="5.5" fill={line} />
          <ellipse cx="75" cy="60" rx="4.5" ry="5.5" fill={line} />
          <circle cx="46.5" cy="58" r="1.4" fill="#fff" />
          <circle cx="76.5" cy="58" r="1.4" fill="#fff" />
        </g>
      )
  }
}

function Mouth({ mood, line }: { mood: CatMood; line: string }) {
  const common = { stroke: line, strokeWidth: 2.2, fill: 'none', strokeLinecap: 'round' as const }
  switch (mood) {
    case 'happy':
      return <path d="M50 80 Q55 88 60 80 Q65 88 70 80" {...common} />
    case 'angry':
      return <path d="M52 86 Q60 80 68 86" {...common} />
    case 'curious':
      return <ellipse cx="60" cy="84" rx="3" ry="3.5" fill={line} />
    default:
      return <path d="M53 81 Q56.5 84 60 80 Q63.5 84 67 81" {...common} />
  }
}
